import { SingleResultApiResponse } from './../../shared/models/api-response.model';
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { MatSort, MatPaginator } from '@angular/material';
import { FormControl } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { ApiResponse } from 'src/app/shared/models/api-response.model';
import { Subscription, fromEvent, merge, forkJoin } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Application } from '../models/application';
import { Action_SetCurrentApplication } from '../state/actions';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { Title } from '@angular/platform-browser';
import { Tab_Title } from '../../shared/models/lookup-data.model';

@Component({
  selector: 'online-app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  host: {
    class:'full-view-port-component-with-no-side-nav dashboard-specific'
  }
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  public displayedColumns: string[] = ['StudentNumber', 'ApplicationNumber', 'GivenName', 'FamilyName',
    'ApplicationStatus', 'CreatedDate', 'ModifiedDate', 'ActionMenu'];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('clearSearchButton') clearSearchButton: ElementRef;
  @ViewChild('resolve') resolve: ElementRef;
  @ViewChild('createApplication') createApplication: ElementRef;
  searchInput: FormControl;

  constructor(private router: Router,
    private modalService: ModalService,
    private applicationService: ApplicationService,
    public sharedService: SharedService,
    private lookupService: LookupService,
    private store: Store<any>,
    private titleService: Title) {
    this.titleService.setTitle(Tab_Title.Dashboard_Title);
    }

  currentUser: any;

  storeSubscription: Subscription;
  subscriptions: Subscription[] = [];
  applications: Application[] = [];
  filteredApplications: Application[] = [];
  pagedSortedApplications: Application[] = [];
  gotApplicationStatuses = false;
  applnIdToBeDeleted = 0;
  applicationAlreadyExists = false;
  showModal = false;
  applicationExpiryOffsetDays: any;
  showDataValidationErrorModal = false;
  dataValidationErrorMsgs = [];
  disableAddNew = false;

  isVisaExpired = false;
  isOSHCExpired = false;
  isStartTermInvalid = false;

  applicationId = 0;

  ngOnInit() {
    this.searchInput = new FormControl(this.sharedService.dashboardSearchTerm);

    this.getApplicationStatuses();
    this.getApplicationDraftExpirySetting();

    this.subscriptions.push(this.store.pipe(select('shared'))
      .subscribe(sharedState => {
        if (sharedState) {
          this.currentUser = sharedState.currentUser;
        } else {
          this.currentUser = null;
        }
      }));

    this.subscriptions.push(this.modalService.allowDelete.subscribe(allowDelete => {

      if (allowDelete && this.applnIdToBeDeleted > 0) {
        this.subscriptions.push(this.applicationService.deleteApplication(this.applnIdToBeDeleted)
          .subscribe(response => {
            this.getApplicationStatuses();
          }, err => console.log(err)));
      } else {
        this.applnIdToBeDeleted = 0;
      }

    }));

  }

  ngAfterViewInit() {

    const clearSearchBtnClick$ = fromEvent(this.clearSearchButton.nativeElement, 'click'); // listen for clicks
    const searchInput$ = this.searchInput.valueChanges
      .pipe(debounceTime(200));

    this.subscriptions.push(
      clearSearchBtnClick$.subscribe(() => {
        this.searchInput.setValue('');
        this.paginator.pageIndex = 0;
      })
    );

    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0)
    );

    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page, searchInput$, clearSearchBtnClick$)
        .subscribe(() => {
          this.filterSortAndPaginate();
        })
    );
  }

  getApplicationStatuses() {
    this.subscriptions.push(this.applicationService.getApplicationStatuses()
      .subscribe((response: ApiResponse) => {
        if (response.Errors && response.Errors.length > 0) {
          //console.log(`Error: ${response.Errors}`);
        } else if (response.Result) {
          this.applications = response.Result;
          this.filterSortAndPaginate();
        }
      }, (error: any) => {
        console.log(error);
      }, () => {
        this.gotApplicationStatuses = true;
      }));
  }

  filterSortAndPaginate() {
    if (!this.applications || !this.applications.length) {
      this.pagedSortedApplications = [];
    }

    const startOffset = this.paginator.pageIndex * this.paginator.pageSize;
    const endOffset = (this.paginator.pageIndex + 1) * this.paginator.pageSize;
    const searchTermLower = this.searchInput.value.toString().toLowerCase();

    this.filteredApplications = this.applications
      .filter(appl => {
        return searchTermLower && searchTermLower.trim().length ?
          appl.StudentNumber.toLowerCase().indexOf(searchTermLower) >= 0 ||
          (appl.GivenName && appl.GivenName.toLowerCase().indexOf(searchTermLower) >= 0) ||
          (appl.FamilyName && appl.FamilyName.toLowerCase().indexOf(searchTermLower) >= 0)
          :
          true;
      });

    this.pagedSortedApplications = this.filteredApplications
      .sort((a, b) => {
        let aVal = a[this.sort.active] || '';
        let bVal = b[this.sort.active] || '';
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();

        if (this.sort.direction === 'asc') {
          return aVal > bVal ? 1 : ((bVal > aVal) ? -1 : 0);
        } else {
          return aVal > bVal ? -1 : ((bVal > aVal) ? 1 : 0);
        }
      })
      .slice(startOffset, endOffset);

    this.updateDashboardOptions();
  }

  updateDashboardOptions() {
    this.sharedService.setDashboardOptions({
      pageNum: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      sortColumn: this.sort.active,
      sortDirection: this.sort.direction,
      searchTerm: this.searchInput.value
    });
  }

  getApplicationDraftExpirySetting() {
    this.subscriptions.push(this.lookupService.getSetting('ApplicationDraftExpirationOffsetDays')
      .subscribe((response: any) => {
        //console.log(response);
        this.applicationExpiryOffsetDays = response.Result.SettingValue;
      }, (error: any) => {
        console.log(error);
      }));
  }

  closeDataValidationErrorModal() {
    this.showDataValidationErrorModal = false;

    if (this.isVisaExpired || this.isOSHCExpired) {
      this.router.navigate(['/applications', this.applicationId, 'edit', 'visa-oshc']);
    } else if (this.isStartTermInvalid) {
      this.router.navigate(['/applications', this.applicationId, 'edit', 'school-program-placement']);
    }

  }

  newApplication(): void {
    if(this.disableAddNew)
      return;
    this.disableAddNew = true;

    this.subscriptions.push(this.applicationService.createApplication()
      .subscribe(response => {
        this.disableAddNew = false;
        this.setCurrentApplicationId(response.Result.ApplicationId);
        this.router.navigate(['/applications', `${response.Result.ApplicationId}`, 'edit', 'important-information']);
      }, (errorResponse: ErrorResponse) => {
        this.disableAddNew = false;

        const { errors } = errorResponse;

        const newApplicationError = errors.find(e => e.Key === 'StudentNumber');

        if (newApplicationError) {
          this.applicationAlreadyExists = true;
          this.showModal = true;
          setTimeout(() => {
            this.createApplication.nativeElement.focus();
          }, 100);
        }

        //console.log('error in creating new application');
        //console.log(errorResponse);
      }));

  }

  displayModal(): boolean {
    return this.applicationAlreadyExists && this.showModal;
  }

  getApplication(currentApplicationId: number): void {
    // this.setCurrentApplicationId(currentApplicationId);

    this.applicationId = currentApplicationId;

    const validations = forkJoin(this.applicationService.validateProgram(this.applicationId),
      this.applicationService.validateVisaOSHC(this.applicationId));

    const validationResponses = [];

    validations.subscribe(responses => {
      for (const response of responses) {
        validationResponses.push(response.Result);
      }
    }, error => {
      return console.log(error);
    }, () => {

      this.isStartTermInvalid = validationResponses[0].IsStartTermInValidForToday;
      this.isVisaExpired = validationResponses[1].IsVisaExpired;
      this.isOSHCExpired = validationResponses[1].isOSHCExpired;

      let visaExpiredMsg = '';
      let oshcExpiredMsg = '';
      let isStartTermInvalidMsg = '';

      if (this.isStartTermInvalid) {
        isStartTermInvalidMsg = `Please note the school start date selected previously is no longer valid.
          Please visit the ‘Student Program and Placement’ section to update your selection. `;
        this.dataValidationErrorMsgs.push(isStartTermInvalidMsg);
      }

      if (this.isVisaExpired) {
        visaExpiredMsg = `Please note that the visa which was previously included in this application has now expired.
           If the student has obtained a new visa or a bridging visa, please provide this information in the “Visa and OSHC’ section.
           If the student no longer has an Australian visa, please also update the ‘Visa and OSHC section.`;
        this.dataValidationErrorMsgs.push(visaExpiredMsg);
      }

      if (this.isOSHCExpired) {
        oshcExpiredMsg = `Please note that the OSHC policy previously provided has expired.
          If this student has purchased a new OSHC policy please update this information in the ‘Visa and OSHC’ section.`;
        this.dataValidationErrorMsgs.push(oshcExpiredMsg);
      }

      this.showDataValidationErrorModal = this.isStartTermInvalid || this.isVisaExpired || this.isOSHCExpired;

      if (!(this.isStartTermInvalid || this.isVisaExpired || this.isOSHCExpired)) {
        this.router.navigate(['/applications', currentApplicationId, 'edit', 'important-information']);
      } else {
        setTimeout(() => {
          this.resolve.nativeElement.focus();
        }, 100);
      }
    });

  }

  getApplicationStatus(applicationId: number): string {

    const application = this.pagedSortedApplications.find(app => app.ApplicationId === applicationId);

    if (application) {
      return application.ApplicationStatusCode.toString();
    }

    return '';
  }

  deleteApplication(event, applicationId: number): void {
    event.stopPropagation();
    this.applnIdToBeDeleted = applicationId;
    this.modalService.showDeleteModal();
  }

  cloneApplication(event, applicationId: number): void {
    event.stopPropagation();
    //this.modalService.showConfirmModal("This action will create a new application by duplicating the selected application.<br/><br/>  You should only use this function if you wish to submit a new application to extend your course.\r\n  You should review and edit the details copied over, before submitting the new application.\r\n  Do you wish to continue?");
    this.modalService.showCopySAModal();

    var subscription = this.modalService.copySAModalResult.subscribe(isConfirm => {
      //Clone application
      if (isConfirm == "Yes" && applicationId > 0) {
        this.subscriptions.push(this.applicationService.cloneSAApplication(applicationId)
          .subscribe(response => {
            this.setCurrentApplicationId(response.Result.ApplicationId);
            this.router.navigate(['/applications', `${response.Result.ApplicationId}`, 'edit', 'important-information']);
          }, (errorResponse: ErrorResponse) => {

            const { errors } = errorResponse;

            const newApplicationError = errors.find(e => e.Key === 'StudentNumber');

            if (newApplicationError) {
              this.applicationAlreadyExists = true;
              this.showModal = true;
              setTimeout(() => {
                this.createApplication.nativeElement.focus();
              }, 100);
            }

            //console.log('error in creating new application');
            //console.log(errorResponse);
          }));
      }
      subscription.unsubscribe();
    })
  }

  clickOK() {
    this.showModal = false;
  }

  setCurrentApplicationId(currentApplicationId: number): void {
    this.store.dispatch({
      type: Action_SetCurrentApplication,
      payload: currentApplicationId
    });
  }

  getApplicationDate(dateInString: string): string {
    const applnDate = new Date(Date.parse(dateInString));
    return applnDate.toLocaleDateString('en-GB');
  }

  /* For accessibility, keyboard only design, use tab to show borders but showing no border by clicking on mouse */
  keyboardFocus(event): void {
    event.style.outline = "1px solid blue";
  }

  keyboardBlur(event): void {
    event.style.outline = "";
  }

  keyboardMatTableFocus(event): void {
    event.srcElement.style.outline = "1px solid blue";
  }

  keyboardMatTableBlur(event): void {
    event.srcElement.style.outline = "";
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

}
